/**
 * ImageSection — Section heading + image + rich-text caption.
 * Layout order is driven by data.layout (['image','caption'] or reversed).
 * Canvas is always read-only. All editing lives in the settings panel.
 */
import React from 'react';
import { resolveAssetUrl } from '../lib/assetUrl';

export function ImageSection({ section, isSelected }) {
  const data = section.data     || {};
  const st   = section.settings || {};

  const heading          = data.heading          || '';
  const imageUrl         = resolveAssetUrl(data.imageUrl) || null;
  const captionHtml      = data.captionHtml      || '';
  const layout           = data.layout           || ['image', 'caption'];
  const captionAlignment = data.captionAlignment || 'left';

  const bg      = st.backgroundColor || '#e9e0cc';
  const textCol = st.textColor        || '#000000';
  const padding = st.padding          || '24px';

  const renderBlock = (item) => {
    if (item === 'image') {
      return imageUrl ? (
        <img
          key="image"
          src={imageUrl}
          alt={heading || 'Section image'}
          style={{ width: '100%', display: 'block', borderRadius: 6 }}
        />
      ) : (
        isSelected && (
          <div key="image-placeholder" style={{
            width: '100%', padding: '40px 0',
            background: 'var(--em-border)', borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: textCol + '88', fontSize: 14, fontStyle: 'italic',
          }}>
            Upload an image in the settings panel
          </div>
        )
      );
    }

    if (item === 'caption') {
      return captionHtml ? (
        <div
          key="caption"
          className="img-caption"
          style={{ fontSize: 14, color: textCol, lineHeight: 1.7, textAlign: captionAlignment }}
          dangerouslySetInnerHTML={{ __html: captionHtml }}
        />
      ) : (
        isSelected && (
          <div key="caption-placeholder" style={{
            fontSize: 14, color: textCol + '88', fontStyle: 'italic',
            textAlign: captionAlignment,
          }}>
            Add a caption in the settings panel
          </div>
        )
      );
    }

    return null;
  };

  const blocks = layout.map(renderBlock).filter(Boolean);

  return (
    <div style={{ padding, backgroundColor: bg }}>
      <style>{`
        .img-caption ul { padding-left: 1.5em; list-style-type: disc; margin: 0.3em 0; }
        .img-caption ol { padding-left: 1.5em; list-style-type: decimal; margin: 0.3em 0; }
        .img-caption li { margin-bottom: 0.2em; }
        .img-caption p  { margin: 0 0 0.3em; }
      `}</style>

      {/* ── Section heading ───────────────────────────────────── */}
      {heading && (
        <div style={{
          fontSize: 22, fontWeight: 700, color: textCol,
          marginBottom: 16, fontFamily: "'Instrument Serif', serif",
        }}>
          {heading}
        </div>
      )}

      {/* ── Image + Caption in layout order ───────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {blocks}
      </div>

      {/* Empty state when not selected */}
      {!heading && !imageUrl && !captionHtml && !isSelected && (
        <div style={{ fontSize: 14, color: textCol + '66', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
          Image section — select to configure
        </div>
      )}
    </div>
  );
}
